import React, {
    ReactElement, useCallback, useEffect, useState,
} from "react";
import { Modal } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { RiCloseLine } from "react-icons/ri";
import TimeAgo from "react-timeago";
import { useKeyboardState } from "../../contexts/KeyboardShortcutContext";
import useEvents from "../../contexts/PortalSubscriptionContext";
import useRouting, { useParams } from "../../contexts/Routing";
import { NoDataOrErrorView } from "../../pages/error/Error";
import {
    ChatStatus, PortalChatResyncEvent, PortalEventType, PortalMessageEvent,
    Ticket as RawTicket, TicketMessage, TicketStatus, useCreateUploadTokenMutation,
    useGetTicketQuery, useMarkReadMutation, UserRole,
} from "../../__generated__/api";
import TextEntry, { UploadedFile } from "../agent/text-entry/text-entry";
import Avatar from "../avatar/Avatar";
import Cards from "../chat/cards";
import Form, { FormDisplayUserType } from "../form/Form";
import Loading from "../loading/Loading";
import Resizable from "../resizable/Resizable";

interface Props {
    id: string;
    onDisplay: (id: string) => void;
}

const STATUS_TEXT = {
    [TicketStatus.Closed]: "Done",
    [TicketStatus.Deflection]: "Open",
    [TicketStatus.Hold]: "Open",
    [TicketStatus.Ignored]: "Done",
    [TicketStatus.Open]: "Open",
    [TicketStatus.Reopened]: "Open",
    [TicketStatus.Snooze]: "Open",
};

export function TicketDisplay({ id, onDisplay }: Props): ReactElement {
    const {
        data, error, loading, refetch,
    } = useGetTicketQuery({
        variables: { id },
        // fetchPolicy: "network-only",
    });
    const { Paths, createHistoryPush } = useRouting();

    const [uploadAttachment] = useCreateUploadTokenMutation();
    const [currentFiles, setCurrentFiles] = useState<UploadedFile[]>([]);

    const [markRead] = useMarkReadMutation();

    const ticket = data?.ticket;
    const [messages, setMessages] = useState(ticket?.messages ?? []);

    const onUploadFiles = useCallback(async (acceptedFiles: File[]) => {
        setCurrentFiles(current => (current ?? []).concat(acceptedFiles.map(file => ({
            name: file.name,
            mimeType: file.type,
            isUploading: true,
        }))));

        const files = await acceptedFiles.mapConcurrent(async (file: File): Promise<UploadedFile> => {
            const meta = await uploadAttachment({
                variables: {
                    attachment: {
                        filename: file.name,
                        size: file.size,
                        mimeType: file.type,
                    },
                },
            });

            if (!meta.data?.createUploadToken || meta.data.createUploadToken?.error) {
                return {
                    name: file.name,
                    mimeType: file.type,
                    error: meta.data?.createUploadToken?.error ?? "Error uploading file",
                };
            }
            const { url, token } = meta.data.createUploadToken;

            const response = await fetch(url, {
                method: "POST",
                body: file,
                credentials: "include",
            });

            if (response.status !== 201) {
                return {
                    name: file.name,
                    mimeType: file.type,
                    error: "Error uploading file. Server did not return 201",
                };
            }

            const json = await response.json() as {url: string};

            return {
                name: file.name,
                mimeType: file.type,
                url: json.url,
                token,
            };
        });

        setCurrentFiles(current => (current ?? []).filter(row => !row.isUploading).concat(files));
    }, []);

    const {
        getRootProps, getInputProps, open, isDragActive,
    } = useDropzone({ onDrop: onUploadFiles, noClick: true });

    useEffect(() => {
        if (ticket?.messages) {
            setMessages(ticket.messages.sortBy("createdDate"));
        }
        if (ticket?.chatId) {
            onDisplay(ticket.chatId);
            (async () => {
                await markRead({
                    variables: {
                        chatIds: [ticket?.chatId],
                    },
                });
            })();
        }
    }, [ticket]);

    useEvents(api => {
        api.on(PortalEventType.Resync, 10, (event: PortalChatResyncEvent) => {
            if (event.chatId === data?.ticket?.chatId) {
                refetch();
            }
        });
        api.on(PortalEventType.ReactionAdded, 10, (event: PortalChatResyncEvent) => {
            if (event.chatId === data?.ticket?.chatId) {
                refetch();
            }
        });
        api.on(PortalEventType.ReactionRemoved, 10, (event: PortalChatResyncEvent) => {
            if (event.chatId === data?.ticket?.chatId) {
                refetch();
            }
        });
        api.on(PortalEventType.MessageNew, 10, (event: PortalMessageEvent) => {
            if (event.chatId === data?.ticket?.chatId) {
                setMessages(current => {
                    const values = current ?? [];
                    return [
                        ...values,
                        {
                            body: event.message,
                            createdDate: event.date,
                            creatorId: event.sender?.id,
                            creator: {
                                id: event.sender?.id,
                                avatarUrl: event.sender?.iconUrl,
                                name: event.sender?.name,
                                userGroups: [],
                                role: UserRole.Agent,
                            },
                            type: event.type,
                            id: event.messageId ?? Date.now(),
                            attachments: event.attachments?.map(attach => ({
                                url: attach.imageUrl,
                                fileName: attach.title,
                                mimeType: attach.mimeType,
                                title: attach.title,
                            })),
                        },
                    ].sortBy("createdDate");
                });
            }
        });
    }, [data?.ticket?.id]);

    if (loading || !ticket) {
        return (
            <div className="d-flex flex-column h-100">
                <div className="card-header bg-gradient text-white d-flex justify-content-between align-items-center">
                    <div>
                        {loading ? "Loading ticket..." : "Error Occured"}
                    </div>
                    <button type="button" className="btn btn-none m-0 p-1 px-2" onClick={createHistoryPush(Paths.TICKETS)}>
                        <RiCloseLine className="text-light" />
                    </button>
                </div>
                <div className="overflow-auto w-100 h-100">
                    <div className={`centered-container-lg m-auto w-100 h-100 flex-stretch-0 `}>
                        {loading ? <Loading /> : (
                            <NoDataOrErrorView error={error} />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const createdDate = ticket.createdDate ? new Date(ticket.createdDate) : undefined;
    const closedDate = ticket.closedDate ? new Date(ticket.closedDate) : undefined;

    return (
        <>

            <div className="card-header bg-gradient text-white d-flex justify-content-between align-items-center">
                <div className="flex-grow-1">
                    {ticket?.ticketNumber && (
                        <span className="me-4 text-light flex-grow-0">
                            #
                            {ticket?.ticketNumber}
                        </span>
                    )}

                    {ticket?.subject}

                </div>

                <button type="button" className="btn btn-none m-0 p-1 px-2" onClick={createHistoryPush(Paths.TICKETS)}>
                    <RiCloseLine className="text-light" />
                </button>

            </div>

            <div className="card-body d-flex w-100 p-0 align-items-stretch align-self-stretch">

                <Resizable
                    direction="e"
                    defaultSize={350}
                >

                    <div className="h-100 d-flex flex-column">

                        <div className="flex-grow-1 overflow-auto h-0">
                            <div className="list-group list-group-flush p-4">

                                <h6 className="col-3">
                                    Context
                                </h6>

                                <div className="mb-1 d-flex justify-content-center">
                                    <small className=" text-gray-500 col-3">
                                        Status
                                    </small>
                                    <div className="col">
                                        {STATUS_TEXT[ticket.status ?? TicketStatus.Open]}
                                    </div>

                                </div>

                                {ticket.assignee && (
                                    <div className="mb-1 d-flex justify-content-center">
                                        <small className=" text-gray-500 col-3">
                                            Assigned
                                        </small>
                                        <div className="col">
                                            <div className="d-flex align-items-center">
                                                <Avatar src={ticket.assignee.avatarUrl as string} variant="xxs" />
                                                <span>
                                                    {ticket.assignee.name}
                                                </span>
                                            </div>
                                        </div>

                                    </div>
                                )}

                                {createdDate && (
                                    <div className="mb-1 d-flex justify-content-center">
                                        <small className=" text-gray-500 col-3">
                                            Created
                                        </small>
                                        <div className="col">
                                            <TimeAgo
                                                date={createdDate}
                                                minPeriod={60}
                                                title={`${createdDate?.toLocaleDateString()}
                                                        ${createdDate?.toLocaleTimeString()}`}
                                            />
                                        </div>
                                    </div>
                                )}

                                {closedDate && (
                                    <div className="mb-1 d-flex justify-content-center">
                                        <small className=" text-gray-500 col-3">
                                            Completed
                                        </small>
                                        <div className="col">
                                            <TimeAgo
                                                date={new Date(closedDate)}
                                                minPeriod={60}
                                                title={`${closedDate.toLocaleDateString()} `
                                                            + `${closedDate.toLocaleTimeString()}`}
                                            />
                                        </div>
                                    </div>
                                )}

                                <h6 className="mt-4">
                                    Request
                                </h6>
                                <Form
                                    ticket={ticket as RawTicket}
                                    isEditable={false}
                                    canSelectRequestType={false}
                                    displayUserType={FormDisplayUserType.REQUESTOR}
                                />
                            </div>

                        </div>
                    </div>

                </Resizable>

                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                <div
                    className="flex-grow-1 col h-100 d-flex flex-column shadow"
                    // eslint-disable-next-line react/jsx-props-no-spreading,
                    {...getRootProps()}

                >
                    {isDragActive && (
                        <div
                            className="fs-4 text-light d-flex justify-content-center align-items-center flex-grow-1"
                        >
                            Drag and Drop files here
                        </div>
                    )}

                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <input {...getInputProps()} />

                    {messages && !isDragActive && (
                        <Cards
                            chatId={data.ticket?.chatId as string}
                            messages={messages as TicketMessage[]}
                        />
                    )}
                    <TextEntry
                        className={isDragActive ? "d-none" : ""}
                        attachments={currentFiles}
                        status={ChatStatus.Assigned}
                        id={ticket.chatId}
                        sourceId=""
                        onOpen={open}
                        canAddNote={false}
                        canBeAssigned={false}
                        onUploadAttachments={onUploadFiles}
                        onSent={() => {
                            // if (chatStatus !== ChatStatus.Assigned && !value.isCurrentUserParticipating) {
                            //     selectionState.setChatStatus(
                            //         selectionState.currentIdOrError,
                            //         ChatStatus.Assigned,
                            //         AssignmentType.CURRENT_USER,
                            //     );
                            // }
                            setCurrentFiles([]);
                        }}
                        onRemoveAttachment={attachments => setCurrentFiles(attachments)}
                        canParticipate={false}
                    />

                </div>

            </div>
        </>
    );
}

TicketDisplay.defaultProps = {
};

const KEYBOARD_CONTEXT = "tickets";
export default function TicketView({ onDisplay, onHide }: {
    onDisplay: (id: string) => void;
    onHide: () => void;
}): ReactElement {
    const { Paths } = useRouting();
    const { ticketId } = useParams(Paths.TICKETS_VIEW);

    const keyboardState = useKeyboardState();
    useEffect(() => {
        if (ticketId === "new") {
            return;
        }
        if (ticketId) {
            keyboardState.pushContext(KEYBOARD_CONTEXT);
        } else {
            keyboardState.popContext(KEYBOARD_CONTEXT);
        }
    }, [ticketId]);

    const shouldShow = ticketId != null && ticketId !== "new";
    useEffect(() => {
        if (!shouldShow) {
            onHide();
        }
    }, [shouldShow]);

    return (
        <Modal
            show={shouldShow}
            size="lg"
            // onHide={() => selectionState.closeCurrent()}
            fullscreen="lg-down"
            dialogClassName="card bg-white shadow border-0 full-size-container"
            contentClassName="h-100 border-0"
            enforceFocus={false}
        >
            <Modal.Body className="h-100 w-100 m-0 p-0 d-flex flex-column">
                {shouldShow && <TicketDisplay id={ticketId} onDisplay={onDisplay} />}
            </Modal.Body>

        </Modal>
    );
}
